"use client";

import { useState } from "react";
import { Slider } from "@velpha/ui/components/slider";
import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    allocation: string;
    defensive: string;
    growth: string;
    rangeHeading: string;
    principalStart: string;
    scaleTick: (yen: number) => string;
    downside: string;
    expected: string;
    upside: string;
    unit: string;
    footnote: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { cash: "預金", bond: "債券", stock: "株式" },
    allocation: "資産配分",
    defensive: "守り重視",
    growth: "成長重視",
    rangeHeading: "100万円の1年後のレンジ",
    principalStart: "100万 スタート",
    scaleTick: (yen) => `${(yen / 10_000).toLocaleString("ja-JP")}万`,
    downside: "下振れ",
    expected: "期待値",
    upside: "上振れ",
    unit: "円",
    footnote:
      "年間目安。株式リスク18% / 債券リスク5% / 相関ゼロの概念値で、実際の商品・期間・相関によって変わります。",
    title: "株式比率を動かして、リスクとリターンの変化を見る",
    description: "スライダーを動かすと、100万円を1年運用したときのレンジがどう変わるかを確認できます。",
  },
  en: {
    assets: { cash: "Deposits", bond: "Bonds", stock: "Equities" },
    allocation: "Asset allocation",
    defensive: "Defensive",
    growth: "Growth",
    rangeHeading: "Range after one year on ¥1,000,000",
    principalStart: "¥1,000,000 start",
    scaleTick: (yen) => `¥${(yen / 1_000).toLocaleString("en-US")}k`,
    downside: "Downside",
    expected: "Expected",
    upside: "Upside",
    unit: "",
    footnote:
      "An annual guide. Conceptual values using 18% equity risk, 5% bond risk, and zero correlation; actual products, periods, and correlations will change these.",
    title: "Move the equity weight and watch risk and return change",
    description: "Move the slider to see how the one-year range on ¥1,000,000 changes.",
  },
};

// ── Asset class assumptions ─────────────────────────────────────────
// 年率の期待リターンとリスク（標準偏差）の概念値。
// 実際の商品や相関、期間によって変わる前提の教育用数値。

const ASSETS = {
  cash: { color: "#a1a1aa", ret: 0.2, risk: 0 },
  bond: { color: COLOR.amber, ret: 3.0, risk: 5 },
  stock: { color: COLOR.sky, ret: 7.0, risk: 18 },
} as const;

const PRINCIPAL = 1_000_000;

// ── Simulator ───────────────────────────────────────────────────────

function AllocationSimulatorInner() {
  const t = STRINGS[useDocLocale()];
  const [stockRatio, setStockRatio] = useState(50);

  const stockPct = stockRatio;
  const bondPct = Math.round((100 - stockRatio) * 0.75);
  const cashPct = 100 - stockPct - bondPct;

  const wReturn =
    (cashPct / 100) * ASSETS.cash.ret +
    (bondPct / 100) * ASSETS.bond.ret +
    (stockPct / 100) * ASSETS.stock.ret;

  // 無相関の前提で加算（債券と株式の間に相関 0 を仮定した簡易計算）
  const wRisk = Math.sqrt((bondPct / 100) ** 2 * ASSETS.bond.risk ** 2 + (stockPct / 100) ** 2 * ASSETS.stock.risk ** 2);

  const expectedYen = Math.round((PRINCIPAL * wReturn) / 100);
  const riskYen = Math.round((PRINCIPAL * wRisk) / 100);

  const lowYen = PRINCIPAL + expectedYen - riskYen;
  const highYen = PRINCIPAL + expectedYen + riskYen;

  const allocations = [
    { ...ASSETS.cash, key: "cash" as const, pct: cashPct },
    { ...ASSETS.bond, key: "bond" as const, pct: bondPct },
    { ...ASSETS.stock, key: "stock" as const, pct: stockPct },
  ];

  // Range scale: ¥500,000 – ¥1,500,000
  const rangeMin = 500_000;
  const rangeMax = 1_500_000;
  const rangeSpan = rangeMax - rangeMin;
  const toPct = (v: number) => ((v - rangeMin) / rangeSpan) * 100;
  const principalLeft = toPct(PRINCIPAL);
  const expectedLeft = toPct(PRINCIPAL + expectedYen);
  const lowLeft = toPct(lowYen);
  const highLeft = toPct(highYen);

  return (
    <div className="space-y-8">
      {/* Allocation zone */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t.allocation}</div>

        {/* Slider */}
        <div className="space-y-1.5">
          <Slider value={[stockRatio]} onValueChange={([v]) => setStockRatio(v)} min={0} max={100} step={5} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t.defensive}</span>
            <span>{t.growth}</span>
          </div>
        </div>

        {/* Allocation bar */}
        <div className="flex h-10 w-full gap-px overflow-hidden rounded-md">
          {allocations.map((a) => (
            <div
              key={a.key}
              className="flex h-full items-center justify-center overflow-hidden whitespace-nowrap text-[11px] font-medium text-white transition-all duration-150"
              style={{ backgroundColor: a.color, width: `${a.pct}%` }}
            >
              {a.pct >= 12 ? (
                <span className="tabular-nums">
                  {t.assets[a.key]} {a.pct}%
                </span>
              ) : a.pct >= 6 ? (
                <span className="tabular-nums">{a.pct}%</span>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Range zone */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t.rangeHeading}
        </div>

        <div className="relative pt-8 pb-6">
          {/* Principal dashed vertical */}
          <div
            className="absolute top-0 bottom-5 -translate-x-1/2"
            style={{ left: `${principalLeft}%` }}
          >
            <div className="h-full w-px border-l border-dashed border-border" />
            <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
              {t.principalStart}
            </div>
          </div>

          {/* Baseline */}
          <div className="absolute inset-x-0 top-[54%] h-px bg-border" />

          {/* Range bar — red below principal, green above principal */}
          {(() => {
            const lossLeft = Math.min(lowLeft, principalLeft);
            const lossRight = Math.min(highLeft, principalLeft);
            const gainLeft = Math.max(lowLeft, principalLeft);
            const gainRight = Math.max(highLeft, principalLeft);
            const hasLoss = lossRight - lossLeft > 0.1;
            const hasGain = gainRight - gainLeft > 0.1;
            return (
              <>
                {hasLoss && (
                  <div
                    className={`absolute h-1.5 bg-rose-500 ${hasGain ? "rounded-l-full" : "rounded-full"}`}
                    style={{
                      left: `${lossLeft}%`,
                      width: `${lossRight - lossLeft}%`,
                      top: "calc(54% - 3px)",
                    }}
                  />
                )}
                {hasGain && (
                  <div
                    className={`absolute h-1.5 bg-emerald-500 ${hasLoss ? "rounded-r-full" : "rounded-full"}`}
                    style={{
                      left: `${gainLeft}%`,
                      width: `${gainRight - gainLeft}%`,
                      top: "calc(54% - 3px)",
                    }}
                  />
                )}
              </>
            );
          })()}

          {/* Range end caps */}
          <div
            className="absolute h-3 w-px bg-rose-500"
            style={{ left: `${lowLeft}%`, top: "calc(54% - 6px)" }}
          />
          <div
            className="absolute h-3 w-px bg-emerald-500"
            style={{ left: `${highLeft}%`, top: "calc(54% - 6px)" }}
          />

          {/* Expected marker */}
          <div
            className="absolute -translate-x-1/2"
            style={{ left: `${expectedLeft}%`, top: "calc(54% - 5px)" }}
          >
            <div className="size-2.5 rounded-full border-2 border-background bg-foreground" />
          </div>

          {/* Scale */}
          <div className="absolute inset-x-0 bottom-0 text-[10px] tabular-nums text-muted-foreground">
            {[500_000, 1_000_000, 1_500_000].map((v) => (
              <span key={v} className="absolute -translate-x-1/2" style={{ left: `${toPct(v)}%` }}>
                {t.scaleTick(v)}
              </span>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 divide-x divide-border border-t border-border -mx-4 bg-background/40 overflow-hidden">
          <div className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-rose-600 dark:text-rose-400">{t.downside}</div>
            <div className="mt-0.5 text-sm font-semibold tabular-nums tracking-tight">
              {lowYen.toLocaleString()}
              <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">{t.unit}</span>
            </div>
          </div>
          <div className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.expected}</div>
            <div className="mt-0.5 text-sm font-semibold tabular-nums tracking-tight">
              {(PRINCIPAL + expectedYen).toLocaleString()}
              <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">{t.unit}</span>
            </div>
          </div>
          <div className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">{t.upside}</div>
            <div className="mt-0.5 text-sm font-semibold tabular-nums tracking-tight">
              {highYen.toLocaleString()}
              <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">{t.unit}</span>
            </div>
          </div>
        </div>

        {/* Footnote inside the zone */}
        <p className="px-4 pb-1 -mx-4 -mb-4 pt-2 text-[10px] leading-relaxed text-muted-foreground/60">
          {t.footnote}
        </p>
      </div>
    </div>
  );
}

export function DocAllocationSimulator() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <AllocationSimulatorInner />
    </IllustrationCard>
  );
}
